import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { UserRole } from "../generated/api-client";
import { useTheme } from "@mui/material";
import toast from "react-hot-toast";

const HomePage: React.FC = () => {
  const { user, logout, inviteUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.BaseUser);
  const [inviteLoading, setInviteLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleOpenInviteDialog = () => {
    setInviteDialogOpen(true);
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteEmail("");
    setInviteRole(UserRole.BaseUser);
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Inserisci un'email");
      return;
    }

    setInviteLoading(true);
    try {
      await inviteUser(inviteEmail.trim(), inviteRole);
      toast.success(`Invito inviato a ${inviteEmail}`);
      handleCloseInviteDialog();
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.detail || "Errore nell'invio dell'invito";
      toast.error(errorMsg);
    } finally {
      setInviteLoading(false);
    }
  };

  const getRoleLabel = (role?: string) => {
    if (!role) return "N/A";
    const roleMap: { [key: string]: string } = {
      baseUser: "Utente Base",
      mantainer: "Mantainer",
      admin: "Amministratore",
    };
    return roleMap[role] || role;
  };

  const isAdmin = user?.role === "admin";

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          Welcome to GreenEye
        </Typography>
        <Button variant="outlined" onClick={handleLogout}>
          Logout
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Informazioni Utente
          </Typography>
          {user?.firstname && user?.lastname && (
            <Typography variant="body1">
              <strong>Nome:</strong> {user.firstname} {user.lastname}
            </Typography>
          )}
          <Typography variant="body1">
            <strong>Email:</strong> {user?.email}
          </Typography>
          <Typography variant="body1">
            <strong>Ruolo:</strong> {getRoleLabel(user?.role)}
          </Typography>
          <Typography variant="body1">
            <strong>Stato:</strong> {user?.is_active ? "Attivo" : "Inattivo"}
          </Typography>
          <Typography variant="body1">
            <strong>Verificato:</strong> {user?.is_verified ? "Sì" : "No"}
          </Typography>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Amministrazione</Typography>
              <Button variant="contained" onClick={handleOpenInviteDialog}>
                Invita Utente
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Invita nuovi utenti al sistema assegnando loro un ruolo specifico.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Getting Started
        </Typography>
        <Typography variant="body1" paragraph>
          This is a GreenEye skeleton with authentication, minimal database
          setup, and Docker configuration. You can use this as a starting point
          for new projects.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Features included:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 3 }}>
          <li>FastAPI backend with JWT authentication</li>
          <li>React frontend with Material-UI</li>
          <li>PostgreSQL database</li>
          <li>Docker and Docker Compose setup</li>
          <li>Well-structured codebase following best practices</li>
        </Box>
      </Paper>

      {/* Dialog per invitare utenti */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invita Nuovo Utente</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="nuovo.utente@example.com"
              fullWidth
              required
            />
            <TextField
              select
              label="Ruolo"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              fullWidth
              required
            >
              <MenuItem value={UserRole.BaseUser}>Utente Base</MenuItem>
              <MenuItem value={UserRole.Mantainer}>Mantainer</MenuItem>
              <MenuItem value={UserRole.Admin}>Amministratore</MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              L'utente riceverà un'email con un link per completare la
              registrazione.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog} disabled={inviteLoading}>
            Annulla
          </Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={inviteLoading || !inviteEmail.trim()}
          >
            {inviteLoading ? <CircularProgress size={24} /> : "Invia Invito"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HomePage;
